import React from 'react';
import styled from '@emotion/styled';
import Layout from '../components/layout/Layout';
import { theme } from '../styles/theme';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: ${theme.spacing['2xl']} ${theme.spacing.xl};
`;

const Title = styled.h1`
  font-size: ${theme.typography.fontSize['3xl']};
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.xl};
`;

const Section = styled.section`
  margin-bottom: ${theme.spacing['2xl']};
`;

const SectionTitle = styled.h2`
  font-size: ${theme.typography.fontSize['xl']};
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.lg};
`;

const Text = styled.p`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.text.secondary};
  line-height: 1.6;
  margin-bottom: ${theme.spacing.md};
`;

const List = styled.ul`
  margin-bottom: ${theme.spacing.lg};
  padding-left: ${theme.spacing.xl};
  
  li {
    color: ${theme.colors.text.secondary};
    margin-bottom: ${theme.spacing.sm};
    line-height: 1.6;
  }
`;

const LastUpdated = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.light};
  margin-top: ${theme.spacing['2xl']};
  text-align: center;
`;

const PrivacyPolicy = () => {
  return (
    <Layout>
      <Container>
        <Title>Privacy Policy</Title>

        <Section>
          <Text>
            At Neat Schedule, we take your privacy seriously. This Privacy Policy explains how we collect, use, 
            disclose, and safeguard your information when you use our scheduling service.
          </Text>
        </Section>

        <Section>
          <SectionTitle>Information We Collect</SectionTitle>
          <Text>We collect information that you provide directly to us, including:</Text>
          <List>
            <li>Name and contact information</li>
            <li>Appointment and scheduling preferences</li>
            <li>Payment information — Card payments are processed securely by Deluxe. We do not store your full card number on our servers; we only retain the last 4 digits for display purposes (e.g., identifying the card on file). All other payment data is handled by our payment processor.</li>
            <li>Communication preferences</li>
            <li>Any other information you choose to provide</li>
          </List>
        </Section>

        <Section>
          <SectionTitle>How We Use Your Information</SectionTitle>
          <Text>We use the information we collect to:</Text>
          <List>
            <li>Provide, maintain, and improve our services</li>
            <li>Process your appointments and payments</li>
            <li>Send you appointment reminders and updates</li>
            <li>Communicate with you about our services</li>
            <li>Protect against fraudulent or unauthorized transactions</li>
            <li>Monitor SMS logs for security purposes, including detecting and preventing abuse, fraud, and unauthorized access to our services</li>
          </List>
        </Section>

        <Section>
          <SectionTitle>Information Sharing</SectionTitle>
          <Text>
            We do not sell, trade, or otherwise transfer your personal information to third parties 
            without your consent, except as described in this Privacy Policy. We may share your 
            information with trusted third parties who assist us in operating our website, conducting 
            our business, or servicing you.
          </Text>
        </Section>

        <Section>
          <SectionTitle>Data Security</SectionTitle>
          <Text>
            We implement appropriate technical and organizational security measures to protect your 
            personal information. However, please note that no method of transmission over the internet 
            or electronic storage is 100% secure.
          </Text>
          <Text>
            As part of our security measures, we monitor SMS communications sent through our platform 
            to detect and prevent fraudulent activity, abuse, and unauthorized access. This monitoring 
            helps us maintain the security and integrity of our services and protect both our users and 
            our platform from potential threats.
          </Text>
        </Section>

        <Section>
          <SectionTitle>Account Access and Support</SectionTitle>
          <Text>
            To provide you with the best possible support and troubleshooting assistance, we maintain 
            an impersonation system that allows our authorized support staff to access your account 
            with your existing permissions. This system enables our team to:
          </Text>
          <List>
            <li>Diagnose and resolve technical issues more effectively</li>
            <li>Provide personalized support and assistance</li>
            <li>Reproduce and fix bugs or errors you may encounter</li>
            <li>Help configure your account settings when requested</li>
          </List>
          <Text>
            <strong>You have full control over this feature.</strong> You can enable or disable 
            account impersonation at any time through your account settings. When impersonation is 
            disabled, our support staff will not be able to access your account, and we will work 
            with you through alternative support methods.
          </Text>
          <Text>
            All impersonation access is logged and monitored for security purposes. Our support staff 
            can only access your account with the same permissions you have—they cannot access 
            information or perform actions beyond your account's normal capabilities.
          </Text>
        </Section>

        <Section>
          <SectionTitle>Your Rights</SectionTitle>
          <Text>You have the right to:</Text>
          <List>
            <li>Access your personal information</li>
            <li>Correct inaccurate information</li>
            <li>Request deletion of your information</li>
            <li>Object to our processing of your information</li>
            <li>Withdraw consent where applicable</li>
          </List>
        </Section>

        <Section>
          <SectionTitle>Contact Us</SectionTitle>
          <Text>
            If you have any questions about this Privacy Policy or our practices, please contact us at{' '}
            <a 
              href="mailto:privacy@neatschedule.com" 
              style={{ color: theme.colors.primary.main }}
            >
              privacy@neatschedule.com
            </a>
          </Text>
        </Section>

        <LastUpdated>
          Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </LastUpdated>
      </Container>
    </Layout>
  );
};

export default PrivacyPolicy; 